import { createContext, useContext, useState, useEffect } from 'react'
import { message } from 'antd'
import api from '../services/api'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('token'))
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user')
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        return JSON.parse(savedUser)
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error)
    }
    return null
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete api.defaults.headers.common['Authorization']
    }
  }, [token])

  const applyAuth = (access_token, userData) => {
    setToken(access_token)
    setUser(userData)
    localStorage.setItem('token', access_token)
    localStorage.setItem('user', JSON.stringify(userData))
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
  }

  const login = async (username, password) => {
    setLoading(true)
    try {
      const response = await api.post('/auth/login', { username, password })
      const { access_token, user: userData } = response.data
      applyAuth(access_token, userData || { name: username })
      message.success('Login successful')
      return true
    } catch (error) {
      message.error(error.response?.data?.error || 'Login failed')
      return false
    } finally {
      setLoading(false)
    }
  }

  const loginWithGoogle = async (credential) => {
    setLoading(true)
    try {
      const response = await api.post('/auth/google', { credential })

      const { access_token, user: userData } = response.data
      applyAuth(access_token, userData)
      message.success(`Welcome ${userData?.name || userData?.email || ''}`)
      return true
    } catch (error) {
      message.error(error.response?.data?.error || 'Login failed')
      return false
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setToken(null)
      setUser(null)
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete api.defaults.headers.common['Authorization']
      message.info('Logged out successfully')
    }
  }

  const value = {
    token,
    user,
    loading,
    login,
    loginWithGoogle,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
